import { useMemo, useState } from 'react';
import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import toSafeInteger from 'lodash/toSafeInteger';
import { emphasizeAndEscapeText } from '@helpcenter/result-base';
import qs from 'query-string';
import { useRouter } from 'react-resource-router';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

export const getSelectedPortalsFromQueryParams = (queryStringParams: { portalIds: string }): string[] => {
    const { portalIds: selectedPortalIds } = queryStringParams;
    if (!selectedPortalIds) {
        return [];
    }

    return Array.isArray(selectedPortalIds) ? selectedPortalIds : [selectedPortalIds];
};

export const getSelectedCategoriesFromQueryParams = (queryStringParams: { categoryIds: string }): string[] => {
    const { categoryIds: selectedCategoryIds } = queryStringParams;
    if (!selectedCategoryIds) {
        return [];
    }

    return Array.isArray(selectedCategoryIds) ? selectedCategoryIds : [selectedCategoryIds];
};

export const formatText = (text: string = '', term: string = '') =>
    emphasizeAndEscapeText(text, term)
        .replace(/@@@hl@@@/g, '<strong>')
        .replace(/@@@endhl@@@/g, '</strong>');

export const formatToCleanText = (text: string = '', term: string = '') =>
    emphasizeAndEscapeText(text, term)
        .replace(/@@@hl@@@/g, '')
        .replace(/@@@endhl@@@/g, '');

export const useQueryParams = () => {
    const [{ location }, _] = useRouter();
    // Suppressing existing violation. Please fix this.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const queryStringParams = useMemo(() => {
        // Suppressing existing violation. Please fix this.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return qs.parse(location.search);
    }, [location.search]);
    const portalIds = useMemo(() => getSelectedPortalsFromQueryParams(queryStringParams), [queryStringParams]);
    const categoryIds = useMemo(() => getSelectedCategoriesFromQueryParams(queryStringParams), [queryStringParams]);
    // Suppressing existing violation. Please fix this.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return { ...queryStringParams, portalIds, categoryIds };
};

export const isSinglePortalSearch = (portalId: string | number | undefined) =>
    portalId !== undefined && getPortalIdAsNumber(portalId) !== 0;

export const isValidSearchTerm = (term: string | undefined) => Boolean(term && term.trim() !== '');

export const getPortalIdAsNumber = (portalId: string | number | null | undefined) => {
    return toSafeInteger(portalId);
};

export const getIdsArray = (ids: string[] | string | number | null | undefined) => {
    if (ids) {
        if (isNumber(ids) || isString(ids)) {
            return [ids.toString()];
        }

        return isArray(ids) ? ids : [];
    }
    return [];
};

export const sendSearchUIAnalyticEvents = (
    eventProperties: {
        action: string;
        actionSubjectId?: string;
    } & Record<string, unknown>,
    createAnalyticsEvent?: CreateUIAnalyticsEvent,
    portalId?: string,
    isInlineResult?: boolean
) => {
    if (isInlineResult) {
        eventProperties.actionSubjectId = 'inlineResult';
    } else {
        eventProperties.actionSubjectId = portalId ? 'singlePortal' : 'portalHomePage';
    }
    createAnalyticsEvent &&
        createAnalyticsEvent({
            analyticsType: 'UI',
            actionSubjectId: eventProperties.actionSubjectId,
            ...eventProperties,
        }).fire();
};

export const sendSearchOperationalAnalyticEvents = (
    eventProperties: { action: string; actionSubjectId?: string; statusCode?: number | null } & Record<string, unknown>,
    createAnalyticsEvent?: CreateUIAnalyticsEvent,
    portalId?: string
) => {
    const statusCode = eventProperties.statusCode;
    if (statusCode !== 403 && statusCode !== 429) {
        createAnalyticsEvent &&
            createAnalyticsEvent({
                analyticsType: 'OPERATIONAL',
                actionSubjectId: portalId ? 'singlePortal' : 'portalHomePage',
                ...eventProperties,
            }).fire();
    }
};

export const useVisibilityState = () => {
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isShortcutIconVisible, setIsShortcutIconVisible] = useState(false);

    return {
        setIsHovered,
        setIsFocused,
        setIsShortcutIconVisible,
        isShortcutIconVisible: isHovered || isFocused || isShortcutIconVisible,
    };
};
